import gql from 'graphql-tag'
import { Injectable } from '@angular/core'
import { Apollo } from 'apollo-angular'
import { map } from 'rxjs/operators'
import { Observable } from 'rxjs'

export interface User {
  id: number;
  email: string;
  adminImpersonating: boolean
  role: string
  showSidebar: boolean;
  showMigrationBanner: boolean;
  uiPreferences: {
    showMigrationAlertOnLoad: boolean
  }
  canPurchasePosts: boolean
  canUpdateCustomer: boolean
  canCreateVideoRequest: boolean
}

export interface Account {
  id: number;
  agency: boolean;
  currentVideoSubscriptionsCount: number;
  tenTopicsPrice: number;
  tenTopicPriceDefault: number;
}

export interface Customer {
  id: number
  multipass: boolean
  balance: number
  state: string
  businessName: string
  displayName: string
  postCreditsCount: number
  postsIncluded: number
  planAddOnCode: string
  hasBpsPreference: boolean
  subscription: {
    id: number
    state: string
    quantity: number
    nextCreditsIssuedAt: Date
    pastDueAt: Date
    extraVideoPrice: number
    plan: {
      id: number
      code: string
      words: number
    }
    planAddOns: {
      id: number
      code: string
    }[]
    scheduledToCancelAt: Date
    scheduledToPauseAt: Date
  }
  scheduledSubscription: {
    id: number
    nextCreditsIssuedAt: Date
    quantity: number
  }
  extraPurchases: {
    purchasedAt: Date
    extra: {
      code: string
    }
  }[]
  requestsCount: number
  activeKeywordsCount: number
  assignedKeywordsCount: number
  activeRequestsCount: number
  proposedKeywordsCount: number
  queuedPostsCount: number
  totalKeywordsPieces: number
  keywordsWithBpsNeedsExist: boolean
}

export const query = gql`
  query DashboardPageQuery($customerId: ID!) {
    currentUser {
      id
      email
      adminImpersonating
      role
      showSidebar
      showMigrationBanner
      uiPreferences {
        showMigrationAlertOnLoad
      }
      extraPurchases {
        id
        purchasedAt
        extra {
          id
          code
        }
      }
      canPurchasePosts: can(action: "post_now", subject: "Post")
      canUpdateCustomer: can(action: "update", subject: "Customer")
      canCreateVideoRequest: can(action: "create", subject: "VideoRequest")
    }
    account {
        id
        agency
        currentVideoSubscriptionsCount
        tenTopicsPrice
        tenTopicPriceDefault
    }
    customer(id: $customerId) {
      id
      multipass
      balance
      state
      businessName
      displayName
      postsIncluded
      postCreditsCount
      planAddOnCode
      hasBpsPreference
      subscription {
        id
        state
        nextCreditsIssuedAt
        pastDueAt
        quantity
        ... on LegacySubscription {
          extraVideoPrice
          plan {
            id
            code
            words
          }
          planAddOns {
            id
            code
          }
        }
        ... on MultipassSubscription {
          scheduledToCancelAt
          scheduledToPauseAt
          selectedProducts {
            id
            code
            name
          }
        }
      }
      scheduledSubscription {
        id
        nextCreditsIssuedAt
        quantity
      }
      extraPurchases {
        id
        extra {
          id
          code
        }
        purchasedAt
      }
      requestsCount
      activeRequestsCount
      activeKeywordsCount
      assignedKeywordsCount
      proposedKeywordsCount
      queuedPostsCount
      totalKeywordsPieces
    }
  }
`

@Injectable({providedIn: 'root'})
export class DashboardQueries {
  constructor(private apollo: Apollo) {}

  dashboardQuery({customerId}: {customerId: number}): Observable<{
    loading: boolean,
    currentUser: User,
    account: Account,
    customer: Customer
  }> {
    return this.apollo.watchQuery<{
      currentUser: User,
      account: Account,
      customer: Customer
    }>({
      query,
      variables: { customerId },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network'
    })
    .valueChanges
    .pipe(
      map(({data, loading}) => ({
        loading,
        currentUser: data && data.currentUser,
        account: data && data.account,
        customer: data && data.customer
      }))
    )
  }
}
